import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { PhotoUploadDialogButton } from "component/shared/photo-upload-dialog-button";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import { fetchEmailBranding, saveEmailBranding } from "redux/slice/email-branding";
import * as api from "api/api";
import { EmailBrandingResource } from "api/resource/email-branding";
import { coldFusionReplatform } from "shared/env";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const TITLE = "Email Branding";

// Preserve the existing page permission predicate on both branches (settings-email-branding stub:20).
const isPermitted = (user: PageProps["user"]) =>
	!!user &&
	(!user.permissions.plugin || (!!user.websitePlatform && !user.websitePlatform.wordpress)) &&
	(!user.permissions.oneCrm || user.admin);

const header = (
	<Mui.Typography variant="h1">
		<FeatherIcon>
			<Icons.Settings />
		</FeatherIcon>
		{TITLE}
	</Mui.Typography>
);

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout permitted={isPermitted(user)} title={TITLE} header={header} iframe={<ColdFusionIframe url="/emailDefaults.cfm" />} />
);

const SettingsEmailBrandingBase: React.FC<Props> = ({ user, classes = {} }) => {
	const dispatch = useDispatch();
	const branding = useSelector((state: RootState) => state.emailBranding.emailBranding);
	const loading = useSelector((state: RootState) => state.emailBranding.loading);

	const [tab, setTab] = useState(0); // 0 = Basic, 1 = Custom
	const [photoUrl, setPhotoUrl] = useState("");
	const [logoUrl, setLogoUrl] = useState("");
	const [name, setName] = useState("");
	const [company, setCompany] = useState("");
	const [phone, setPhone] = useState("");
	const [addressLine1, setAddressLine1] = useState("");
	const [addressLine2, setAddressLine2] = useState("");
	const [customHeaderHtml, setCustomHeaderHtml] = useState("");
	const [customFooterHtml, setCustomFooterHtml] = useState("");
	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
		open: false,
		message: "",
		severity: "success",
	});

	useEffect(() => {
		dispatch(fetchEmailBranding() as any);
	}, [dispatch]);

	const applyBranding = React.useCallback((b: EmailBrandingResource) => {
		setTab(b.type === "custom" ? 1 : 0);
		setPhotoUrl(b.photoUrl || "");
		setLogoUrl(b.logoUrl || "");
		setName(b.name || "");
		setCompany(b.company || "");
		setPhone(b.phone || "");
		setAddressLine1(b.addressLine1 || "");
		setAddressLine2(b.addressLine2 || "");
		setCustomHeaderHtml(b.customHeaderHtml || "");
		setCustomFooterHtml(b.customFooterHtml || "");
		setDirty(false);
	}, []);

	useEffect(() => {
		if (branding) {
			applyBranding(branding);
		}
	}, [branding, applyBranding]);

	const notify = (message: string, severity: "success" | "error") =>
		setSnackbar({ open: true, message, severity });

	const buildBody = (): EmailBrandingResource => ({
		type: tab === 1 ? "custom" : "basic",
		photoUrl,
		logoUrl,
		name,
		company,
		phone,
		addressLine1,
		addressLine2,
		customHeaderHtml,
		customFooterHtml,
		// read-only on write; server recomputes eligibility.
		emailBrandingEnabled: branding ? branding.emailBrandingEnabled : true,
	});

	const handleSave = async () => {
		setSaving(true);
		try {
			// F4: .unwrap() so an API failure rejects into catch instead of firing the success notify.
			await dispatch(saveEmailBranding(buildBody()) as any).unwrap();
			notify("Email branding saved", "success");
			setDirty(false);
		} catch{
			notify("Failed to save email branding", "error");
		} finally {
			setSaving(false);
		}
	};

	const handleCancel = () => {
		if (branding) {
			applyBranding(branding);
		}
	};

	const handlePreview = async () => {
		try {
			const html = await api.previewEmailBranding(buildBody());
			const blob = new Blob([html], { type: "text/html" });
			const url = URL.createObjectURL(blob);
			window.open(url, "_blank");
		} catch{
			notify("Failed to generate preview", "error");
		}
	};

	// Second gate: non-responsive-email clients keep the legacy CF screen even under the flag.
	if (branding && !branding.emailBrandingEnabled) {
		return (
			<DashboardLayout permitted={isPermitted(user)} title={TITLE} header={header} iframe={<ColdFusionIframe url="/emailDefaults.cfm" />} />
		);
	}

	return (
		<DashboardLayout permitted={isPermitted(user)} title={TITLE} header={header}>
			{loading && <Mui.LinearProgress />}

			<FullPageCard>
				<Mui.Tabs
					className={classes.tabs}
					value={tab}
					onChange={(_event, value) => {
						setTab(value);
						setDirty(true);
					}}
					indicatorColor="primary"
					textColor="primary"
				>
					<Mui.Tab label="Basic" data-testid="email-branding-tab-basic" />
					<Mui.Tab label="Custom" data-testid="email-branding-tab-custom" />
				</Mui.Tabs>

				{tab === 0 ? (
					<Mui.Grid container direction="column" spacing={3}>
						<Mui.Grid item container spacing={4}>
							<Mui.Grid item className={classes.uploadRow}>
								<Mui.Typography variant="subtitle2">Photo</Mui.Typography>
								{photoUrl && <img src={photoUrl} alt="Email branding photo" className={classes.previewImage} />}
								<PhotoUploadDialogButton
									label="Upload Photo"
									variant="outlined"
									color="primary"
									callback={(files) => {
										if (files && files[0]) {
											setPhotoUrl(files[0].url);
											setDirty(true);
										}
									}}
								/>
							</Mui.Grid>
							<Mui.Grid item className={classes.uploadRow}>
								<Mui.Typography variant="subtitle2">Logo</Mui.Typography>
								{logoUrl && <img src={logoUrl} alt="Email branding logo" className={classes.previewImage} />}
								<PhotoUploadDialogButton
									label="Upload Logo"
									variant="outlined"
									color="primary"
									callback={(files) => {
										if (files && files[0]) {
											setLogoUrl(files[0].url);
											setDirty(true);
										}
									}}
								/>
							</Mui.Grid>
						</Mui.Grid>

						<Mui.Grid item container spacing={2}>
							<Mui.Grid item xs={12} sm={6}>
								<Mui.TextField
									fullWidth label="Name" variant="outlined" size="small"
									value={name}
									onChange={(e) => { setName(e.target.value); setDirty(true); }}
									inputProps={{ "data-testid": "email-branding-name" }}
								/>
							</Mui.Grid>
							<Mui.Grid item xs={12} sm={6}>
								<Mui.TextField
									fullWidth label="Company" variant="outlined" size="small"
									value={company}
									onChange={(e) => { setCompany(e.target.value); setDirty(true); }}
								/>
							</Mui.Grid>
							<Mui.Grid item xs={12} sm={6}>
								<Mui.TextField
									fullWidth label="Address Line 1" variant="outlined" size="small"
									value={addressLine1}
									onChange={(e) => { setAddressLine1(e.target.value); setDirty(true); }}
								/>
							</Mui.Grid>
							<Mui.Grid item xs={12} sm={6}>
								<Mui.TextField
									fullWidth label="Address Line 2" variant="outlined" size="small"
									value={addressLine2}
									onChange={(e) => { setAddressLine2(e.target.value); setDirty(true); }}
								/>
							</Mui.Grid>
							<Mui.Grid item xs={12} sm={6}>
								<Mui.TextField
									fullWidth label="Phone" variant="outlined" size="small"
									value={phone}
									onChange={(e) => { setPhone(e.target.value); setDirty(true); }}
								/>
							</Mui.Grid>
						</Mui.Grid>

						<Mui.Grid item>
							<Mui.Button variant="outlined" onClick={handlePreview} data-testid="email-branding-preview">
								Preview
							</Mui.Button>
						</Mui.Grid>
					</Mui.Grid>
				) : (
					<Mui.Grid container direction="column" spacing={3}>
						<Mui.Grid item>
							<Mui.Typography variant="body2" color="textSecondary" className={classes.helperText}>
								Content must be wrapped in &lt;div&gt; tags. All links, scripts, and images must use absolute URLs.
							</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.TextField
								fullWidth multiline minRows={6} label="Email Header" variant="outlined"
								value={customHeaderHtml}
								onChange={(e) => { setCustomHeaderHtml(e.target.value); setDirty(true); }}
								inputProps={{ "data-testid": "email-branding-header" }}
							/>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.TextField
								fullWidth multiline minRows={6} label="Email Footer" variant="outlined"
								value={customFooterHtml}
								onChange={(e) => { setCustomFooterHtml(e.target.value); setDirty(true); }}
								inputProps={{ "data-testid": "email-branding-footer" }}
							/>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Button variant="outlined" onClick={handlePreview} data-testid="email-branding-preview">
								Preview
							</Mui.Button>
						</Mui.Grid>
					</Mui.Grid>
				)}
			</FullPageCard>

			<SettingsStickyFooter dirty={dirty} saving={saving} onSave={handleSave} onCancel={handleCancel} />

			<Mui.Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
			>
				<MuiAlert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
					{snackbar.message}
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const SettingsEmailBranding = withStyles(SettingsEmailBrandingBase, styles);

const SettingsEmailBrandingPageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <SettingsEmailBranding {...props} />;
};

export const SettingsEmailBrandingPage = withStyles(SettingsEmailBrandingPageBase, styles);
